import * as Sentry from '@sentry/react';
import { useQuery } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import {
  type DeviceActivityLog,
  type GetDeviceActivityLogsResponse,
  getDeviceActivityLogs,
} from 'api/admin/washboard/washboard';

type UseWashboardDeviceActivityLogsParams = {
  deviceId: number;
  page: number;
  limit: number;
};

type UseWashboardDeviceActivityLogsData = {
  activityLogs: DeviceActivityLog[];
  hasMore: boolean;
  total: number;
};

export const useWashboardDeviceActivityLogs = ({
  deviceId,
  page,
  limit,
}: UseWashboardDeviceActivityLogsParams): {
  data?: UseWashboardDeviceActivityLogsData;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: AxiosError<{ error: string }> | null;
  refetch: () => void;
} => {
  const result = useQuery<UseWashboardDeviceActivityLogsData, AxiosError<{ error: string }>>({
    queryKey: ['washboardDeviceActivityLogs', deviceId, page, limit],
    queryFn: async () => {
      const { data } = (await getDeviceActivityLogs(deviceId, {
        page,
        limit,
      })) as AxiosResponse<GetDeviceActivityLogsResponse>;

      return {
        activityLogs: data.activityLogs,
        hasMore: data.hasMore,
        total: data.total,
      };
    },
    onError: (error) => {
      Sentry.captureException(error);
    },
    enabled: !!deviceId,
    retry: false,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  return {
    data: result.data,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    isError: result.isError,
    error: result.error,
    refetch: result.refetch,
  };
};
